import {
    IEventrActivity,
    V2,
    filterEntitiesToPastAndPresentWP,
    structuredCloneL,
} from '@eventr/common'
import { useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useGuestStore } from '../../feat/guest/guest.store'
import { useBoundStore } from '../../state/bound.store'
import { filterOutPendingTag } from '../../utils'
import { ScreenType } from '../renderers/rendererLocator'

export const usePastPresentElements = (
    data: V2 | null,
    screenType: ScreenType
) => {
    const isGuestMode = useGuestStore((s) => s.isGuestMode)
    const { wpUser, filterEntitiesUserSpecific } = useBoundStore(
        useShallow((s) => {
            return {
                wpUser: s.wpUser,
                filterEntitiesUserSpecific: s.filterEntitiesUserSpecific,
            }
        })
    )

    const { pastElements, presentElements } = useMemo(() => {
        if (!data) {
            return { pastElements: [], presentElements: [] }
        }

        let user = null
        if (isGuestMode) {
            user = {
                id: -1,
                firstName: 'Guest',
                lastName: 'User',
                email: 'guest@example.com',
                department: 'Guest',
                roles: [],
                phoneNumber: '',
            }
        } else {
            user = wpUser
        }

        if (user?.id) {
            const stands: any[] = []
            const dataClone = structuredCloneL(data)
            if (dataClone.sectionID === 8) {
                // get stands for section 8
                dataClone.data.activities.forEach((item: IEventrActivity) => {
                    if (item.type === 'stand') {
                        stands.push(item)
                    }
                })
                dataClone.data.activities = dataClone.data.activities.filter(
                    (item: IEventrActivity) => item.type !== 'stand'
                )
            }
            const filteredData: any[] = filterEntitiesUserSpecific(
                screenType,
                dataClone,
                dataClone.rendererOptions?.settings?.userList
            )

            const past = []
            const present = []

            const timeSorted = filterEntitiesToPastAndPresentWP(
                filteredData,
                user,
                dataClone.rendererOptions?.settings,
                screenType
            )

            past.push(...timeSorted.past)
            present.push(...timeSorted.present)
            // dont show stands in team page
            if (screenType !== 'team') present.push(...stands)

            let pastFilterPending = past.filter(filterOutPendingTag)
            let presentFilterPending = present.filter(filterOutPendingTag)

            if (screenType === 'edit' || screenType === 'team') {
                let uniqueIds: any = {}
                pastFilterPending = pastFilterPending.filter((item) => {
                    if (!uniqueIds[item._id]) {
                        uniqueIds[item._id] = true
                        return true
                    }
                    return false
                })

                uniqueIds = {}
                presentFilterPending = presentFilterPending.filter((item) => {
                    if (!uniqueIds[item._id]) {
                        uniqueIds[item._id] = true
                        return true
                    }
                    return false
                })
            }
            return {
                pastElements: pastFilterPending,
                presentElements: presentFilterPending,
            }
        }
        return {
            pastElements: [],
            presentElements: [],
        }
    }, [data, screenType, isGuestMode, wpUser, filterEntitiesUserSpecific])

    // Return the memoized results directly
    return {
        pastElements,
        presentElements,
    }
}
